/**
 * Vista del presupuesto para el cliente — `TDS-CORE` Parte 2 §4; diseño del
 * bloque 1 (2026-09-18).
 *
 * Es lo que se imprime o se guarda en PDF con el diálogo del navegador (no hay
 * generador de PDF en V1). Todos los importes vienen ya calculados de la API:
 * aquí no se multiplica ni se suma nada, sólo se les da formato de TEXTO.
 *
 * `OD-0029`: TOTAL y "a cobrar" son dos filas distintas. El total no resta la
 * retención (AEAT-REF-012); lo que paga el cliente, sí.
 *
 * Un presupuesto NO lleva el QR de VERI*FACTU: no es un documento fiscal, y un
 * QR de la AEAT aquí confundiría a quien lo recibe.
 */
import { t } from "../i18n/es";
import type { DatosFiscalesCopia, Presupuesto } from "../api/cliente";
import { cantidadLegible, diaDeDocumento, ibanLegible, importeLegible, precioLegible, sinCerosSobrantes } from "./formato";

type Totales = NonNullable<Presupuesto["totals"]>;

function etiquetaDesglose(d: Totales["breakdown"][number]): string {
  if (d.treatment === "SUJETA_NO_EXENTA") return t.productos.iva(sinCerosSobrantes(d.rate ?? ""));
  return d.treatment === "SUJETA_EXENTA" ? t.productos.exenta(d.cause ?? "") : t.productos.noSujeta(d.cause ?? "");
}

/** Lo descontado en la línea `i`, o vacío si no lleva descuento. */
function descuentoDeLinea(totals: Presupuesto["totals"], i: number): string {
  const d = totals?.lines[i]?.discount;
  return d === undefined || d === "0,00" ? "" : `−${importeLegible(d)}`;
}

function formaDePago(clave: string | null | undefined): string | null {
  if (clave === null || clave === undefined) return null;
  const textos: Readonly<Record<string, string>> = t.emisor.formasPago;
  return textos[clave] ?? null;
}

export function DocumentoPresupuesto({
  quote,
  emisorProvisional = null,
}: {
  quote: Presupuesto;
  /**
   * Datos del emisor de HOY, para revisar un borrador: todavía no hay copia
   * congelada (eso pasa al marcarlo como enviado), y un documento sin emisor no
   * sirve para revisarlo. Nunca sustituye a la copia si ya existe.
   */
  emisorProvisional?: DatosFiscalesCopia | null;
}) {
  const totals = quote.totals;
  const emisor = quote.issuer ?? emisorProvisional;
  // Sin esta columna, un precio de 3.000 con un importe de 2.850 parece un
  // error de cuentas. El descuento es la diferencia, y tiene que verse.
  const hayDescuentos = (totals?.lines ?? []).some((l) => l.discount !== "0,00");
  const pago = formaDePago(emisor?.paymentMethod);
  const iban = emisor?.iban ?? null;
  const cliente = quote.customer;

  return (
    <article className="documento">
      <header className="documento-cabecera">
        <h2 className="documento-tipo">{t.presupuesto.documento.tipo}</h2>
        <dl className="documento-datos">
          <div>
            <dt>{t.presupuesto.documento.numeroEtiqueta}</dt>
            <dd>{quote.number}</dd>
          </div>
          <div>
            <dt>{t.presupuesto.documento.fechaEtiqueta}</dt>
            <dd>{diaDeDocumento(quote.issueDate)}</dd>
          </div>
          {quote.validUntil !== null ? (
            <div>
              <dt>{t.presupuesto.documento.validezEtiqueta}</dt>
              <dd>{diaDeDocumento(quote.validUntil)}</dd>
            </div>
          ) : null}
        </dl>
      </header>

      <div className="documento-partes">
        <section className="documento-caja">
          <h3>{t.presupuesto.documento.emisor}</h3>
          {emisor !== null ? (
            <>
              <p className="documento-nombre">{emisor.legalName}</p>
              <p>{emisor.taxIdNormalized}</p>
              <p>{emisor.addressLine}</p>
              <p>
                {emisor.postalCode} {emisor.city} ({emisor.province})
              </p>
              {emisor.contactEmail ? <p>{emisor.contactEmail}</p> : null}
              {emisor.contactPhone ? <p>{emisor.contactPhone}</p> : null}
            </>
          ) : (
            <p className="ayuda-neutra">{t.presupuesto.emisorFalta}</p>
          )}
        </section>
        <section className="documento-caja">
          <h3>{t.presupuesto.documento.cliente}</h3>
          {cliente !== null ? (
            <>
              <p className="documento-nombre">{cliente.legalName ?? cliente.displayName}</p>
              {cliente.taxIdNormalized ? <p>{cliente.taxIdNormalized}</p> : null}
              {cliente.addressLine ? <p>{cliente.addressLine}</p> : null}
              {cliente.postalCode || cliente.city ? (
                <p>
                  {cliente.postalCode ?? ""} {cliente.city ?? ""}
                  {cliente.province ? ` (${cliente.province})` : ""}
                </p>
              ) : null}
            </>
          ) : null}
        </section>
      </div>

      <table className="documento-lineas">
        <thead>
          <tr>
            <th scope="col">{t.presupuesto.documento.concepto}</th>
            <th scope="col">{t.presupuesto.documento.cantidad}</th>
            <th scope="col">{t.presupuesto.documento.precio}</th>
            {hayDescuentos ? <th scope="col">{t.presupuesto.documento.descuento}</th> : null}
            <th scope="col">{t.presupuesto.documento.base}</th>
          </tr>
        </thead>
        <tbody>
          {quote.lines.map((l, i) => (
            <tr key={`${l.description}-${i}`}>
              <td>{l.description}</td>
              <td>{cantidadLegible(l.quantity)}</td>
              <td>{`${precioLegible(l.unitPrice)} €`}</td>
              {hayDescuentos ? <td>{descuentoDeLinea(totals, i)}</td> : null}
              <td>{totals === null ? "" : `${importeLegible(totals.lines[i]?.base ?? "")} €`}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="documento-resumen">
        <section className="documento-caja documento-pago">
          {pago !== null || iban !== null ? (
            <>
              <h3>{t.presupuesto.documento.formaPago}</h3>
              {pago !== null ? <p className="documento-nombre">{pago}</p> : null}
              {iban !== null ? <p>{t.presupuesto.documento.iban(ibanLegible(iban))}</p> : null}
            </>
          ) : null}
        </section>

        {totals !== null ? (
          <section className="documento-caja documento-totales" aria-label={t.presupuesto.totalesTitulo}>
            <dl>
              <div>
                <dt>{t.presupuesto.documento.baseImponible}</dt>
                <dd>{importeLegible(totals.baseTotal)} €</dd>
              </div>
              {totals.breakdown.map((d) => (
                <div key={`${d.treatment}-${d.rate ?? d.cause ?? ""}`}>
                  <dt>{etiquetaDesglose(d)}</dt>
                  <dd>{d.vatAmount === null ? "—" : `${importeLegible(d.vatAmount)} €`}</dd>
                </div>
              ))}
              <div className="documento-total">
                <dt>{t.presupuesto.totalFactura}</dt>
                <dd>{importeLegible(totals.invoiceTotal)} €</dd>
              </div>
              {totals.withholdingRate !== null ? (
                <>
                  <div>
                    <dt>{t.presupuesto.documento.irpf(sinCerosSobrantes(totals.withholdingRate))}</dt>
                    <dd>−{importeLegible(totals.withholdingAmount)} €</dd>
                  </div>
                  <div className="documento-total">
                    <dt>{t.presupuesto.totalACobrar}</dt>
                    <dd>{importeLegible(totals.amountToCollect)} €</dd>
                  </div>
                </>
              ) : null}
            </dl>
          </section>
        ) : (
          <p className="ayuda-neutra">
            {quote.pendingWithholdingDecision ? t.presupuesto.pendienteRetencion : t.presupuesto.sinTotales}
          </p>
        )}
      </div>

      {hayDescuentos ? <p className="ayuda-neutra">{t.presupuesto.documento.ayudaImporte}</p> : null}
      {quote.notes !== null ? <p className="documento-notas">{quote.notes}</p> : null}
      {emisor?.documentFooter ? <p className="documento-pie">{emisor.documentFooter}</p> : null}
      <p className="ayuda-neutra">{t.presupuesto.documento.nota}</p>
      {!quote.frozen ? <p className="ayuda-neutra sin-imprimir">{t.presupuesto.documento.borrador}</p> : null}
    </article>
  );
}
